import { PhotoUpload } from '@/components/features/WorkOrders/components/PhotoUpload';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import { Loader2, Upload } from 'lucide-react';
import { useState } from 'react';

interface PhotoUploadDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload?: (photos: File[]) => void;
    maxPhotos?: number;
}

export function PhotoUploadDialog({ isOpen, onClose, onUpload, maxPhotos = 10 }: PhotoUploadDialogProps) {
    const { t } = useTranslations();
    const [photos, setPhotos] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    const handleUpload = async () => {
        if (photos.length === 0) {
            alert(t('work_orders.show.photos.upload', 'Please select at least one photo'));
            return;
        }

        setIsUploading(true);

        // Simulate API call
        setTimeout(() => {
            onUpload?.(photos);
            setIsUploading(false);
            setPhotos([]);
            onClose();
        }, 1000);
    };

    const handleClose = () => {
        if (!isUploading) {
            setPhotos([]);
            onClose();
        }
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleClose} variant="sheet">
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('work_orders.show.actions.upload_photos', 'Upload Photos')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('work_orders.forms.create.photos.description', 'Upload photos to help describe the work needed')}.{' '}
                        {t('work_orders.forms.create.photos.hint', 'Max {maxPhotos} photos, 10MB each. Supported: JPEG, PNG, WebP').replace(
                            '{maxPhotos}',
                            String(maxPhotos),
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody>
                    <PhotoUpload photos={photos} onPhotosChange={setPhotos} maxPhotos={maxPhotos} />
                </ResponsiveModalBody>

                <ResponsiveModalFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={handleClose} disabled={isUploading} className="w-full sm:w-auto">
                        {t('work_orders.dialogs.complete.cancel', 'Cancel')}
                    </Button>
                    <Button type="button" onClick={handleUpload} disabled={isUploading || photos.length === 0} className="w-full sm:w-auto">
                        {isUploading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('work_orders.show.photos.upload', 'Uploading...')}
                            </>
                        ) : (
                            <>
                                <Upload className="mr-2 h-4 w-4" />
                                {t('work_orders.show.actions.upload_photos', 'Upload')} {photos.length}{' '}
                                {photos.length !== 1 ? t('work_orders.show.photos.upload', 'Photos') : t('work_orders.show.photos.upload', 'Photo')}
                            </>
                        )}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
